import { Check, ChevronDown, Download, ExternalLink, LoaderCircle, Play, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { UserRole } from '../../common/enum/user-role.enum';
import { t } from '../i18n';
import { useStore } from '../hooks/use-store';
import { $aiSelectionPrompt, $currentUser, $useAiMediaSelection } from '../store/app.store';
import type { Episode, SubjectDetail } from '../types/bgm';
import type {
  AiMediaSelectionResult,
  AiPublicSettings,
  MediaResourcePlayResult,
  MediaSearchResult,
} from '../types/page';
import { useApp } from './app-context';
import { Alert } from './alert';
import { Button } from './button';
import { Dialog } from './dialog';

const mediaResourceSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '-';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const power = bytes > 0 ? Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1) : 0;
  const value = bytes / 1024 ** power;
  return `${value >= 100 || Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)} ${units[power]}`;
};

interface MediaSourceDialogProps {
  subject: Pick<SubjectDetail, 'id' | 'name' | 'name_cn'>;
  episode?: Episode;
  onCancel: () => void;
  onPlay: (result: MediaResourcePlayResult, episode: Episode) => void | Promise<void>;
}

const mediaSearchInput = (subject: MediaSourceDialogProps['subject'], episode: Episode) => ({
  subject: {
    id: subject.id,
    name: subject.name,
    nameCn: subject.name_cn || undefined,
  },
  episode: {
    id: episode.id,
    number: episode.ep ?? episode.sort,
    sort: episode.sort,
    name: episode.name || undefined,
    nameCn: episode.name_cn || undefined,
  },
});

export const MediaSourceDialog = ({ subject, episode, onCancel, onPlay }: MediaSourceDialogProps) => {
  const { api, navigate, showToast } = useApp();
  const currentUser = useStore($currentUser);
  const useAiMediaSelection = useStore($useAiMediaSelection);
  const aiSelectionPrompt = useStore($aiSelectionPrompt);
  const [result, setResult] = useState<MediaSearchResult>();
  const [searching, setSearching] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [aiSelecting, setAiSelecting] = useState(false);
  const [aiFallback, setAiFallback] = useState(false);
  const [playingResourceId, setPlayingResourceId] = useState<string>();
  const [collapsedProviders, setCollapsedProviders] = useState<Set<string>>(() => new Set());
  const aiSelectionControllerRef = useRef<AbortController | undefined>(undefined);
  const onPlayRef = useRef(onPlay);
  onPlayRef.current = onPlay;
  const resourceGroups = useMemo(() => {
    const groups = new Map<string, MediaSearchResult['resources']>();
    for (const resource of result?.resources ?? []) {
      const resources = groups.get(resource.providerId) ?? [];
      resources.push(resource);
      groups.set(resource.providerId, resources);
    }
    for (const resources of groups.values()) {
      resources.sort((left, right) => Number(right.downloaded) - Number(left.downloaded));
    }
    return [...groups.entries()];
  }, [result]);

  useEffect(() => {
    aiSelectionControllerRef.current?.abort();
    aiSelectionControllerRef.current = undefined;
    if (!episode) {
      setResult(undefined);
      setSearching(false);
      setAiAvailable(false);
      setAiSelecting(false);
      setAiFallback(false);
      setPlayingResourceId(undefined);
      setCollapsedProviders(new Set());
      return;
    }
    let active = true;
    const controller = new AbortController();
    setResult(undefined);
    setSearching(true);
    setAiAvailable(false);
    setAiSelecting(false);
    setAiFallback(false);
    setPlayingResourceId(undefined);
    setCollapsedProviders(new Set());
    const searchInput = mediaSearchInput(subject, episode);
    const aiAvailability = api<AiPublicSettings>('/api/ai-settings', { signal: controller.signal })
      .then((settings) => settings.available)
      .catch(() => false);
    void aiAvailability.then((available) => {
      if (active) {
        setAiAvailable(available);
      }
    });
    const search = async (): Promise<void> => {
      if (useAiMediaSelection && (await aiAvailability)) {
        if (!active) {
          return;
        }
        setAiSelecting(true);
        try {
          const selectionResult = await api<AiMediaSelectionResult>('/api/ai/media-selection', {
            method: 'POST',
            signal: controller.signal,
            body: JSON.stringify({
              ...searchInput,
              prompt: aiSelectionPrompt.trim() || undefined,
            }),
          });
          if (!active) return;
          setResult(selectionResult.searchResult);
          setAiSelecting(false);
          if (selectionResult.selection) {
            setPlayingResourceId(selectionResult.selection.resource.id);
            showToast(t('preparingMediaResource'));
            try {
              const playResult = await api<MediaResourcePlayResult>(
                `/api/media-resources/${selectionResult.selection.resource.id}/play`,
                { method: 'POST', signal: controller.signal },
              );
              if (active) await onPlayRef.current(playResult, episode);
              return;
            } catch (reason) {
              if (!active) return;
              showToast(reason instanceof Error ? reason.message : String(reason), 'error');
              setPlayingResourceId(undefined);
            }
          }
          setAiFallback(true);
          return;
        } catch {
          if (!active) return;
          setAiSelecting(false);
          setAiFallback(true);
        }
      }
      const nextResult = await api<MediaSearchResult>('/api/media-search', {
        method: 'POST',
        signal: controller.signal,
        body: JSON.stringify(searchInput),
      });
      if (active) setResult(nextResult);
    };
    void search()
      .catch((reason: unknown) => {
        if (active) {
          showToast(reason instanceof Error ? reason.message : String(reason), 'error');
        }
      })
      .finally(() => {
        if (active) {
          setSearching(false);
        }
      });
    return () => {
      active = false;
      controller.abort();
      aiSelectionControllerRef.current?.abort();
      aiSelectionControllerRef.current = undefined;
    };
  }, [aiSelectionPrompt, api, episode, showToast, subject.id, subject.name, subject.name_cn, useAiMediaSelection]);

  const toggleProvider = (providerId: string): void => {
    setCollapsedProviders((current) => {
      const next = new Set(current);
      if (next.has(providerId)) {
        next.delete(providerId);
      } else {
        next.add(providerId);
      }
      return next;
    });
  };

  const play = async (resourceId: string): Promise<void> => {
    if (!episode || playingResourceId) {
      return;
    }
    setPlayingResourceId(resourceId);
    showToast(t('preparingMediaResource'));
    try {
      const playResult = await api<MediaResourcePlayResult>(`/api/media-resources/${resourceId}/play`, {
        method: 'POST',
      });
      await onPlay(playResult, episode);
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
      setPlayingResourceId(undefined);
    }
  };

  const selectWithAi = async (): Promise<void> => {
    if (!episode || !aiAvailable || aiSelecting || playingResourceId) {
      return;
    }
    const controller = new AbortController();
    aiSelectionControllerRef.current?.abort();
    aiSelectionControllerRef.current = controller;
    setAiSelecting(true);
    setAiFallback(false);
    try {
      const selectionResult = await api<AiMediaSelectionResult>('/api/ai/media-selection', {
        method: 'POST',
        signal: controller.signal,
        body: JSON.stringify({
          ...mediaSearchInput(subject, episode),
          prompt: aiSelectionPrompt.trim() || undefined,
        }),
      });
      if (controller.signal.aborted) {
        return;
      }
      setResult(selectionResult.searchResult);
      if (!selectionResult.selection) {
        setAiFallback(true);
        return;
      }
      await play(selectionResult.selection.resource.id);
    } catch {
      if (!controller.signal.aborted) {
        setAiFallback(true);
      }
    } finally {
      if (aiSelectionControllerRef.current === controller) {
        aiSelectionControllerRef.current = undefined;
        setAiSelecting(false);
      }
    }
  };

  return (
    <Dialog
      open={Boolean(episode)}
      title={`${t('mediaResources')} · EP.${episode?.sort ?? ''}`}
      description={
        <div className="media-search-content overflow-y-hidden">
          <p>{t('mediaResourcesDescription')}</p>
          {aiFallback && !searching ? (
            <Alert className="media-search-warning" variant="warning">
              {t('aiSelectionFallback')}
            </Alert>
          ) : null}
          {searching ? (
            <div className="media-search-loading">
              <LoaderCircle className="spin" />
              <span>{t(aiSelecting ? 'aiSelectingMedia' : 'searchingMediaResources')}</span>
            </div>
          ) : null}
          {result?.errors.length ? (
            <Alert className="media-search-warning" variant="warning">
              {t('mediaSourcePartialFailure')}: {result.errors.map((item) => item.providerId).join(', ')}
            </Alert>
          ) : null}
          {!searching && result && resourceGroups.length === 0 ? (
            <div className="media-search-empty">{t('noMediaResources')}</div>
          ) : null}
          {resourceGroups.map(([providerId, resources]) => {
            const collapsed = collapsedProviders.has(providerId);
            return (
              <section className="media-resource-group" data-collapsed={collapsed} key={providerId}>
                <h3>
                  <Button
                    variant="plain"
                    className="media-resource-group-toggle"
                    aria-expanded={!collapsed}
                    onClick={() => toggleProvider(providerId)}
                  >
                    <span>
                      {providerId}
                      <small>{resources.length}</small>
                    </span>
                    <ChevronDown />
                  </Button>
                </h3>
                <div className="media-resource-group-content" aria-hidden={collapsed} inert={collapsed}>
                  <div>
                    <div className="media-resource-list">
                      {resources.map((resource) => (
                        <article className="media-resource-row" key={resource.id}>
                          <div className="media-resource-main">
                            <strong title={resource.title}>{resource.title}</strong>
                            <div className="media-resource-links">
                              {resource.downloaded ? (
                                <span className="media-resource-downloaded">
                                  <Check />
                                  {t('mediaResourceDownloaded')}
                                </span>
                              ) : null}
                              {resource.size !== undefined && resource.size >= 1024 ? (
                                <span className="media-resource-size">{mediaResourceSize(resource.size)}</span>
                              ) : null}
                              <a href={resource.resourceUrl} target="_blank" rel="noreferrer">
                                {resource.resourceUrl.startsWith('magnet:') ? <ExternalLink /> : <Download />}
                                {resource.resourceUrl.startsWith('magnet:') ? 'Magnet' : 'Torrent'}
                              </a>
                              {resource.sourcePageUrl ? (
                                <a href={resource.sourcePageUrl} target="_blank" rel="noreferrer">
                                  <ExternalLink />
                                  {t('sourcePage')}
                                </a>
                              ) : null}
                            </div>
                          </div>
                          <Button
                            compact
                            disabled={aiSelecting || Boolean(playingResourceId)}
                            onClick={() => void play(resource.id)}
                          >
                            {playingResourceId === resource.id ? (
                              <LoaderCircle className="size-3.5! spin" />
                            ) : (
                              <Play className="size-3.5!" />
                            )}
                            {t('playMediaResource')}
                          </Button>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      }
      hideConfirm
      loading={Boolean(playingResourceId)}
      footerAction={
        (aiAvailable && !useAiMediaSelection) || currentUser?.role === UserRole.ADMIN ? (
          <div className="flex items-center gap-2">
            {aiAvailable && !useAiMediaSelection ? (
              <Button
                compact
                loading={aiSelecting}
                disabled={searching || !result?.resources.length || Boolean(playingResourceId)}
                onClick={() => void selectWithAi()}
              >
                <Sparkles className="size-3.5!" />
                {t('aiHelpSelect')}
              </Button>
            ) : null}
            {currentUser?.role === UserRole.ADMIN ? (
              <Button compact onClick={() => navigate('/settings/subscriptions')}>
                <ExternalLink className="size-3.5!" />
                {t('mediaSourceSettings')}
              </Button>
            ) : null}
          </div>
        ) : undefined
      }
      panelClassName="media-search-dialog-panel"
      onCancel={onCancel}
      onConfirm={onCancel}
    />
  );
};
